import { supabase } from '../lib/supabase';

// =====================================================
// FILE UPLOAD SERVICE USING SUPABASE STORAGE
// =====================================================

export interface UploadResult {
  success: boolean;
  filePath?: string;
  fileUrl?: string;
  error?: string;
}

export interface KYCUploadData {
  userId: string;
  documentType: 'id_document' | 'license_document' | 'proof_of_address';
  file: File;
}

/**
 * Uploads a KYC document to Supabase Storage
 * @param uploadData - The upload data containing userId, documentType, and file
 * @returns Promise<UploadResult> - The upload result with file path and URL
 */
export const uploadKYCFile = async (uploadData: KYCUploadData): Promise<UploadResult> => {
  try {
    const { userId, documentType, file } = uploadData;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Invalid file type. Please upload JPEG, PNG, or PDF files only.'
      };
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size too large. Please upload files smaller than 10MB.'
      };
    }

    // Generate secure file path
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${userId}/${documentType}/${timestamp}_${sanitizedFileName}`;

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('kyc-documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Supabase Storage upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error.message}`
      };
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('kyc-documents')
      .getPublicUrl(filePath);

    return {
      success: true,
      filePath: data.path,
      fileUrl: urlData.publicUrl
    };

  } catch (error) {
    console.error('File upload error:', error);
    return {
      success: false,
      error: 'An unexpected error occurred during upload'
    };
  }
};

/**
 * Deletes a KYC document from Supabase Storage
 * @param filePath - The file path in storage
 * @returns Promise<boolean> - Success status
 */
export const deleteKYCFile = async (filePath: string): Promise<boolean> => {
  try {
    const { error } = await supabase.storage
      .from('kyc-documents')
      .remove([filePath]);

    if (error) {
      console.error('Supabase Storage delete error:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('File delete error:', error);
    return false;
  }
};

/**
 * Gets a signed URL for private file access (if needed)
 * @param filePath - The file path in storage
 * @param expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns Promise<string | null> - The signed URL or null if error
 */
export const getSignedUrl = async (filePath: string, expiresIn: number = 3600): Promise<string | null> => {
  try {
    const { data, error } = await supabase.storage
      .from('kyc-documents')
      .createSignedUrl(filePath, expiresIn);

    if (error) {
      console.error('Supabase Storage signed URL error:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Signed URL error:', error);
    return null;
  }
};

/**
 * Lists files for a specific user and document type
 * @param userId - The user ID
 * @param documentType - The document type (optional)
 * @returns Promise<string[]> - Array of file paths
 */
export const listUserKYCFiles = async (userId: string, documentType?: string): Promise<string[]> => {
  try {
    const folderPath = documentType ? `${userId}/${documentType}` : userId;

    const { data, error } = await supabase.storage
      .from('kyc-documents')
      .list(folderPath);

    if (error) {
      console.error('Supabase Storage list error:', error);
      return [];
    }

    return data.map(file => `${folderPath}/${file.name}`);
  } catch (error) {
    console.error('List files error:', error);
    return [];
  }
};

/**
 * Validates file before upload
 * @param file - The file to validate
 * @returns Validation result with success status and error message
 */
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Invalid file type. Please upload JPEG, PNG, or PDF files only.'
    };
  }

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size too large. Please upload files smaller than 10MB.'
    };
  }

  // Check file name length
  if (file.name.length > 255) {
    return {
      isValid: false,
      error: 'File name too long. Please use a shorter name.'
    };
  }

  return { isValid: true };
};

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Gets file extension from file name
 * @param fileName - The file name
 * @returns File extension (without dot)
 */
export const getFileExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() || '';
};

/**
 * Checks if file is an image
 * @param file - The file to check
 * @returns True if file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * Checks if file is a PDF
 * @param file - The file to check
 * @returns True if file is a PDF
 */
export const isPDFFile = (file: File): boolean => {
  return file.type === 'application/pdf';
};
